import { FC, useState } from 'react';

import { TableColumnsType } from 'antd/lib';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import useSWR from 'swr';

import IconDown from '@/assets/icons/icon-down.svg?react';
import IconRight from '@/assets/icons/icon-right.svg?react';
import Table from '@/components/RTable';
import useCurrency from '@/contexts/useCurrency';
import {
  SimulationResult,
  SimulationResultYearlyFinancialAnalysis,
  SimulationResultYearlyFinancialAnalysisData,
} from '@/types/simulations';
import { converToMillion } from '@/utils';

import StyledProfit from './styles';

interface DataType {
  key: string;
  name: string | JSX.Element;
  total: string;
  [key: string]: string | number | JSX.Element | undefined;
}

type ProfitAndLossTableProps = {
  simulationId?: number;
};

const ProfitAndLossTable: FC<ProfitAndLossTableProps> = ({ simulationId }) => {
  const { t } = useTranslation();
  const { currencySymbol } = useCurrency();
  const params = useParams<Record<string, string>>();
  const resultId = simulationId || params.resultId;
  const { data: financialData } =
    useSWR<SimulationResultYearlyFinancialAnalysis>(
      `/simulation/${resultId}/yearly_financial_analysis/`
    );

  const { data: simulationData } = useSWR<SimulationResult>(
    `/simulation/${resultId}/`
  );

  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onExpand = (expanded: boolean, record: any) => {
    const keys = expanded
      ? [...expandedRowKeys, record.key]
      : expandedRowKeys.filter((key) => key !== record.key);
    setExpandedRowKeys(keys);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const customExpandIcon = (props: any) => {
    if (!props.record.children) {
      return <span style={{ marginRight: '22px' }} />;
    }
    if (props.expanded) {
      return (
        <IconDown
          width={12}
          height={12}
          style={{ marginRight: '8px' }}
          onClick={(e) => props.onExpand(props.record, e)}
        />
      );
    } else {
      return (
        <IconRight
          width={12}
          height={12}
          style={{ marginRight: '8px' }}
          onClick={(e) => props.onExpand(props.record, e)}
        />
      );
    }
  };

  let years: number[] = [];

  if (financialData?.graphData) {
    years = Array.from(
      new Set(financialData.graphData.map((item) => item.year))
    ).sort((a, b) => a - b);
  }

  const scrollX = 775 + years.length * 100;

  if (!financialData || !simulationData) {
    return null;
  }

  const columns: TableColumnsType<DataType> = [
    {
      title: '',
      dataIndex: 'name',
      key: 'name',
      fixed: 'left',
      width: 350,
    },
    {
      title: t('result.finance.profitAndLoss.tables.columns.total'),
      dataIndex: 'total',
      key: 'total',
      width: 100,
    },
    ...years.map((year) => ({
      title: year.toString(),
      dataIndex: year.toString(),
      key: year.toString(),
      width: 100,
    })),
  ];

  const getValuesForYears = (
    graphData: SimulationResultYearlyFinancialAnalysisData[],
    key: string
  ) => {
    const values: Record<string, string | number> = {};
    if (graphData) {
      years.forEach((year) => {
        const data = graphData.find((item) => item.year === year);
        values[year.toString()] = data
          ? converToMillion(data[key]) + ` ${currencySymbol}`
          : '0';
      });
    }
    return values;
  };

  financialData.graphData.forEach((item) => {
    const totalDepreciation = item.storageDepreciation + item.plantDepreciation;
    item.totalDepreciation = totalDepreciation;
  });

  const dataSource = [
    {
      key: '1',
      name: t('result.finance.profitAndLoss.tables.rows.revenues'),
      total:
        converToMillion(simulationData?.result.totalRevenue) +
        ` ${currencySymbol}`,
      ...getValuesForYears(financialData.graphData, 'totalRevenue'),
      children: [
        {
          key: '1-1',
          name: t('result.finance.profitAndLoss.tables.rows.energyRevenues'),
          total:
            converToMillion(simulationData?.result.totalRevenueFromSale) +
            ` ${currencySymbol}`,
          ...getValuesForYears(financialData.graphData, 'totalRevenueFromSale'),
          children: [
            {
              key: '1-1-1',
              name: t(
                'result.finance.profitAndLoss.tables.rows.revenueFromDischarge'
              ),
              total:
                converToMillion(simulationData?.result.revenueFromDischarge) +
                ` ${currencySymbol}`,
              ...getValuesForYears(
                financialData.graphData,
                'revenueFromDischarge'
              ),
              children: [
                {
                  key: '1-1-1-1',
                  name: t(
                    'result.finance.profitAndLoss.tables.rows.revenueFromDischargePlant'
                  ),
                  total:
                    converToMillion(
                      simulationData?.result.revenueFromDischargePlant
                    ) + ` ${currencySymbol}`,
                  ...getValuesForYears(
                    financialData.graphData,
                    'revenueFromDischargePlant'
                  ),
                },
                {
                  key: '1-1-1-2',
                  name: t(
                    'result.finance.profitAndLoss.tables.rows.revenueFromDischargePurchase'
                  ),
                  total:
                    converToMillion(
                      simulationData?.result.revenueFromDischargePurchased
                    ) + ` ${currencySymbol}`,
                  ...getValuesForYears(
                    financialData.graphData,
                    'revenueFromDischargePurchased'
                  ),
                },
              ],
            },
            {
              key: '1-1-2',
              name: t(
                'result.finance.profitAndLoss.tables.rows.revenueFromPlant'
              ),
              total:
                converToMillion(simulationData?.result.revenueFromPlant) +
                ` ${currencySymbol}`,
              ...getValuesForYears(financialData.graphData, 'revenueFromPlant'),
            },
          ],
        },
        {
          key: '1-2',
          name: t('result.finance.profitAndLoss.tables.rows.capacityRevenues'),
          total:
            converToMillion(simulationData?.result.revenueFromOfferedCapacity) +
            ` ${currencySymbol}`,
          ...getValuesForYears(
            financialData.graphData,
            'revenueFromOfferedCapacity'
          ),
          children: [
            {
              key: '1-2-1',
              name: t(
                'result.finance.profitAndLoss.tables.rows.revenueFromPfc'
              ),
              total:
                converToMillion(
                  simulationData?.result.revenueFromOfferedCapacityOfPfc
                ) + ` ${currencySymbol}`,
              ...getValuesForYears(
                financialData.graphData,
                'revenueFromOfferedCapacityOfPfc'
              ),
            },
            {
              key: '1-2-2',
              name: t(
                'result.finance.profitAndLoss.tables.rows.revenueFromSfc'
              ),
              total:
                converToMillion(
                  simulationData?.result.revenueFromOfferedCapacityOfSfc
                ) + ` ${currencySymbol}`,
              ...getValuesForYears(
                financialData.graphData,
                'revenueFromOfferedCapacityOfSfc'
              ),
            },
          ],
        },
      ],
    },
    // {
    //   key: '13',
    //   name: t('result.finance.profitAndLoss.tables.rows.residualValues'),
    //   total: converToMillion(simulationData?.result.totalResidualValue) + ` ${currencySymbol}`,
    //   ...getValuesForYears(financialData.graphData, 'totalResidualValue'),
    //   children: [
    //     {
    //       key: '13-1',
    //       name: t(
    //         'result.finance.profitAndLoss.tables.rows.plantResidualValue'
    //       ),
    //       total:
    //         converToMillion(simulationData?.result.residualValueOfPlant) + ` ${currencySymbol}`,
    //       ...getValuesForYears(financialData.graphData, 'residualValueOfPlant'),
    //     },
    //     {
    //       key: '13-2',
    //       name: t(
    //         'result.finance.profitAndLoss.tables.rows.batteryResidualValue'
    //       ),
    //       total:
    //         converToMillion(simulationData?.result.residualValueOfBattery) +
    //         ` ${currencySymbol}`,
    //       ...getValuesForYears(
    //         financialData.graphData,
    //         'residualValueOfBattery'
    //       ),
    //     },
    //     {
    //       key: '13-3',
    //       name: t('result.finance.profitAndLoss.tables.rows.bosResidualValue'),
    //       total:
    //         converToMillion(simulationData?.result.residualValueOfBos) + ` ${currencySymbol}`,
    //       ...getValuesForYears(financialData.graphData, 'residualValueOfBos'),
    //     },
    //   ],
    // },
    {
      key: '2',
      name: t('result.finance.profitAndLoss.tables.rows.costOfPurchasedEnergy'),
      total:
        converToMillion(simulationData?.result.costOfPurchasedEnergy) +
        ` ${currencySymbol}`,
      ...getValuesForYears(financialData.graphData, 'costOfPurchasedEnergy'),
    },
    {
      key: '10',
      name: t(
        'result.finance.profitAndLoss.tables.rows.prePlantConsumptionBillingCost'
      ),
      total:
        converToMillion(simulationData?.result.preplantConsumptionBillingCost) +
        ` ${currencySymbol}`,
      ...getValuesForYears(
        financialData.graphData,
        'preplantConsumptionBillingCost'
      ),
    },
    {
      key: '14',
      name: t('result.finance.profitAndLoss.tables.rows.distributionCosts'),
      total:
        converToMillion(simulationData?.result.distributionCosts) +
        ` ${currencySymbol}`,
      ...getValuesForYears(financialData.graphData, 'distributionCosts'),
      children: [
        {
          key: '14-1',
          name: t(
            'result.finance.profitAndLoss.tables.rows.distributionCostOfPurchasedEnergy'
          ),
          total:
            converToMillion(
              simulationData?.result.distributionCostOfPurchasedEnergy
            ) + ` ${currencySymbol}`,
          ...getValuesForYears(
            financialData.graphData,
            'distributionCostOfPurchasedEnergy'
          ),
          children: [
            {
              key: '14-1-1',
              name: t(
                'result.finance.profitAndLoss.tables.rows.distributionCostOfPurchasedEnergyForConsumption'
              ),
              total:
                converToMillion(
                  simulationData?.result
                    .distributionCostOfPurchasedEnergyForConsumption
                ) + ` ${currencySymbol}`,
              ...getValuesForYears(
                financialData.graphData,
                'distributionCostOfPurchasedEnergyForConsumption'
              ),
            },
            {
              key: '14-1-2',
              name: t(
                'result.finance.profitAndLoss.tables.rows.distributionCostOfPurchasedEnergyForArbitrage'
              ),
              total:
                converToMillion(
                  simulationData?.result
                    .distributionCostOfPurchasedEnergyForArbitrage
                ) + ` ${currencySymbol}`,
              ...getValuesForYears(
                financialData.graphData,
                'distributionCostOfPurchasedEnergyForArbitrage'
              ),
            },
            {
              key: '14-1-3',
              name: t(
                'result.finance.profitAndLoss.tables.rows.prePlantDistributionCostOfPurchasedEnergy'
              ),
              total:
                converToMillion(
                  simulationData?.result
                    .preplantDistributionCostOfPurchasedEnergy
                ) + ` ${currencySymbol}`,
              ...getValuesForYears(
                financialData.graphData,
                'preplantDistributionCostOfPurchasedEnergy'
              ),
            },
          ],
        },
        {
          key: '14-2',
          name: t(
            'result.finance.profitAndLoss.tables.rows.distributionCostOfSoldEnergy'
          ),
          total:
            converToMillion(
              simulationData?.result.distributionCostOfSoldEnergy
            ) + ` ${currencySymbol}`,
          ...getValuesForYears(
            financialData.graphData,
            'distributionCostOfSoldEnergy'
          ),
          children: [
            {
              key: '14-2-1',
              name: t(
                'result.finance.profitAndLoss.tables.rows.distributionCostOfSoldEnergyFromPlant'
              ),
              total:
                converToMillion(
                  simulationData?.result
                    .distributionCostOfSoldEnergyDirectlyFromPlant
                ) + ` ${currencySymbol}`,
              ...getValuesForYears(
                financialData.graphData,
                'distributionCostOfSoldEnergyDirectlyFromPlant'
              ),
            },
            {
              key: '14-2-2',
              name: t(
                'result.finance.profitAndLoss.tables.rows.distributionCostOfSoldEnergyFromStorage'
              ),
              total:
                converToMillion(
                  simulationData?.result.distributionCostOfSoldEnergyFromStorage
                ) + ` ${currencySymbol}`,
              ...getValuesForYears(
                financialData.graphData,
                'distributionCostOfSoldEnergyFromStorage'
              ),
            },
          ],
        },
      ],
    },

    {
      key: '3',
      name: t('result.finance.profitAndLoss.tables.rows.grossProfit'),
      total:
        converToMillion(simulationData?.result.grossProfit) +
        ` ${currencySymbol}`,
      ...getValuesForYears(financialData.graphData, 'grossProfit'),
    },
    {
      key: '4',
      name: t('result.finance.profitAndLoss.tables.rows.operatingExpenses'),
      total:
        converToMillion(simulationData?.result.totalOpex) +
        ` ${currencySymbol}`,
      ...getValuesForYears(financialData.graphData, 'totalOpex'),
      children: [
        {
          key: '4-2',
          name: t(
            'result.finance.profitAndLoss.tables.rows.storageSystemFixedOpex'
          ),
          total:
            converToMillion(simulationData?.result.storageSystemFixedOpex) +
            ` ${currencySymbol}`,
          ...getValuesForYears(
            financialData.graphData,
            'storageSystemFixedOpex'
          ),
        },
        {
          key: '4-3',
          name: t(
            'result.finance.profitAndLoss.tables.rows.powerPlantFixedOpex'
          ),
          total:
            converToMillion(simulationData?.result.powerPlantFixedOpex) +
            ` ${currencySymbol}`,
          ...getValuesForYears(financialData.graphData, 'powerPlantFixedOpex'),
        },
      ],
    },
    {
      key: '5',
      name: t('result.finance.profitAndLoss.tables.rows.ebitda'),
      total:
        converToMillion(simulationData?.result.ebitda) + ` ${currencySymbol}`,
      ...getValuesForYears(financialData.graphData, 'ebitda'),
    },
    {
      key: '6',
      name: t('result.finance.profitAndLoss.tables.rows.depreciation'),
      ...getValuesForYears(financialData.graphData, 'totalDepreciation'),
      children: [
        {
          key: '6-1',
          name: t(
            'result.finance.profitAndLoss.tables.rows.storageSystemDepreciation'
          ),
          ...getValuesForYears(financialData.graphData, 'storageDepreciation'),
        },
        {
          key: '6-2',
          name: t(
            'result.finance.profitAndLoss.tables.rows.powerPlantDepreciation'
          ),
          ...getValuesForYears(financialData.graphData, 'plantDepreciation'),
        },
      ],
    },
    {
      key: '7',
      name: t('result.finance.profitAndLoss.tables.rows.ebit'),
      total:
        converToMillion(simulationData?.result.ebit) + ` ${currencySymbol}`,
      ...getValuesForYears(financialData.graphData, 'ebit'),
    },
    {
      key: '12',
      name: t('result.finance.profitAndLoss.tables.rows.interestExpense'),
      total:
        converToMillion(simulationData?.result.totalLoanInterestPayment) +
        ` ${currencySymbol}`,
      ...getValuesForYears(financialData.graphData, 'totalLoanInterestPayment'),
    },
    {
      key: '8',
      name: t('result.finance.profitAndLoss.tables.rows.taxes'),
      total:
        converToMillion(simulationData?.result.incomeTax) +
        ` ${currencySymbol}`,
      ...getValuesForYears(financialData.graphData, 'incomeTax'),
    },
    {
      key: '9',
      name: t('result.finance.profitAndLoss.tables.rows.netIncome'),
      total:
        converToMillion(simulationData?.result.netIncome) +
        ` ${currencySymbol}`,
      ...getValuesForYears(financialData.graphData, 'netIncome'),
    },
  ];

  return (
    <StyledProfit>
      <Table
        dataSource={dataSource}
        columns={columns}
        expandable={{
          expandedRowKeys,
          onExpand,
          expandIcon: customExpandIcon,
          rowExpandable: (record) => !!record.children,
        }}
        pagination={false}
        scroll={{ x: scrollX }}
      />
    </StyledProfit>
  );
};

export default ProfitAndLossTable;
